import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform, Alert } from 'react-native';
import theme from '../../config/theme';
import FormattedInput from '../FormattedInput';
import cep from 'cep-promise';
import { useWebAlert } from '../WebAlert';
import { ChecklistCEPFieldLogger } from '@/utils/logger';

/**
 * Tipos de CEP suportados no template
 *
 * cep1 – CEP (campo principal)
 * cep2 – Endereço (rua)
 * cep3 – Número (não é preenchido automaticamente)
 * cep4 – Bairro
 * cep5 – Cidade
 * cep6 – UF
 */

interface ChecklistCEPFieldProps {
  hideLabel?: boolean;
  /** Campo recebido do template (deve ser do tipo 'cep1') */
  field: {
    id: string;
    nome: string;
  };
  /** Valor atual do CEP (com máscara) */
  value: string | undefined;
  /** Atualiza o próprio campo (cep1) */
  onChangeSelf: (val: string) => void;
  /** Função genérica para atualizar QUALQUER campo do checklist */
  updateField: (fieldId: string, value: string) => void;
  /** Ids opcionais dos campos correlatos que podem ser preenchidos automaticamente */
  enderecoId?: string;
  bairroId?: string;
  cidadeId?: string;
  ufId?: string;
}

const ChecklistCEPField: React.FC<ChecklistCEPFieldProps> = ({
  field,
  hideLabel = false,
  value = '',
  onChangeSelf,
  updateField,
  enderecoId,
  bairroId,
  cidadeId,
  ufId,
}) => {
  const [loading, setLoading] = useState(false);
  const { showAlert } = useWebAlert();

  // Função de alerta cross-plataforma
  const showMessage = useCallback((title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg);
    }
  }, [showAlert]);

  /**
   * Handler ao digitar no campo CEP
   * – Aplica máscara automaticamente (usando FormattedInput)
   * – Quando atingir 8 dígitos, busca o endereço via serviço externo
   */
  const handleChange = async (text: string) => {
    ChecklistCEPFieldLogger.Main.info(`[handleChange] Digitado: ${text}`);
    onChangeSelf(text);

    // Remove caracteres não numéricos para checar tamanho
    const digits = text.replace(/\D/g, '');
    if (digits.length !== 8) {
      ChecklistCEPFieldLogger.Main.info('[handleChange] Aguardando 8 dígitos para buscar CEP');
      return;
    }

    ChecklistCEPFieldLogger.Main.info(`[handleChange] Iniciando busca para CEP=${digits}`);
    setLoading(true);
    try {
      // Timeout de 3 segundos para evitar esperas longas
      const result: any = await Promise.race([
        cep(digits, { timeout: 3000 }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout na busca do CEP')), 3000)),
      ]);

      if (result && typeof result === 'object') {
        ChecklistCEPFieldLogger.Main.info(`[handleChange] Resultado CEP: ${JSON.stringify(result)}`);
        // Preencher campos correlatos caso existam
        if (enderecoId) {
          ChecklistCEPFieldLogger.Main.info(`[handleChange] Autopreenchendo enderecoId=${enderecoId}`);
          updateField(enderecoId, result.street || '');
        }
        if (bairroId) {
          ChecklistCEPFieldLogger.Main.info(`[handleChange] Autopreenchendo bairroId=${bairroId}`);
          updateField(bairroId, result.neighborhood || '');
        }
        if (cidadeId) {
          ChecklistCEPFieldLogger.Main.info(`[handleChange] Autopreenchendo cidadeId=${cidadeId}`);
          updateField(cidadeId, result.city || '');
        }
        if (ufId) {
          ChecklistCEPFieldLogger.Main.info(`[handleChange] Autopreenchendo ufId=${ufId}`);
          updateField(ufId, result.state || '');
        }
      }
    } catch (err: any) {
      ChecklistCEPFieldLogger.Main.info(`[handleChange] Erro ao buscar CEP: ${err}`);
      showMessage('CEP não encontrado', 'Não foi possível obter o endereço para o CEP informado. Preencha manualmente.');
    } finally {
      setLoading(false);
      ChecklistCEPFieldLogger.Main.info('[handleChange] Busca CEP finalizada');
    }
  };

  return (
    <View style={styles.container}>
      {!hideLabel && <Text style={styles.label}>{field.nome}</Text>}
      <View style={styles.inputWrapper}>
        <FormattedInput
          type="cep"
          value={value || ''}
          onChangeText={handleChange}
          style={styles.input}
          placeholder="00000-000"
          placeholderTextColor={theme.colors.text.tertiary}
        />
        {loading && (
          <ActivityIndicator style={styles.spinner} size="small" color={theme.colors.primary.default} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.components.checklistEditor.spacing.labelGap,
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  spinner: {
    position: 'absolute',
    right: theme.spacing.sm,
    top: '50%',
    marginTop: -9,
  },
});

export default ChecklistCEPField;
